import { useState, useEffect } from 'react';
import { DashboardT } from 'business/definitions';
import { useToggleFavouriteDashboard } from 'routes/Home/hooks/favouriteDashboards';

/**
 * Hook to manage the presentation mode state of a dashboard.
 *
 * @param dashboard - The dashboard object.
 * @param onPropertyChange - Optional callback to handle property changes.
 * @returns An object containing the current presentation mode state and a function to toggle it.
 */
export const usePresentationModeDashboardState = (
    dashboard: DashboardT | null | undefined,
    onPropertyChange?: (
        dashboard: DashboardT,
        property: keyof DashboardT,
        value: boolean,
    ) => void,
) => {
    const [isInPresentationMode, setIsInPresentationMode] = useState(
        dashboard?.isInPresentationMode,
    );

    useEffect(() => {
        setIsInPresentationMode(dashboard?.isInPresentationMode);
    }, [dashboard?.isInPresentationMode]);

    const togglePresentationMode = () => {
        if (onPropertyChange && dashboard) {
            onPropertyChange(
                dashboard,
                'isInPresentationMode',
                !isInPresentationMode,
            );
        }
        setIsInPresentationMode((prevState) => !prevState);
    };

    return {
        isInPresentationMode,
        togglePresentationMode,
    };
};

/**
 * Hook to manage the favourite state of a dashboard.
 *
 * @param dashboard - The dashboard object.
 * @param onPropertyChange - Optional callback to handle property changes.
 * @returns An object containing the current favourite state and a function to toggle it.
 */
export const useFavouriteDashboardState = (
    dashboard: DashboardT | null | undefined,
    onPropertyChange?: (
        dashboard: DashboardT,
        property: keyof DashboardT,
        value: boolean,
    ) => void,
) => {
    const [isFavourite, setIsFavourite] = useState(dashboard?.isUserFavourite);

    useEffect(() => {
        setIsFavourite(dashboard?.isUserFavourite);
    }, [dashboard?.isUserFavourite]);

    const onSuccess = () => {
        if (onPropertyChange && dashboard) {
            onPropertyChange(dashboard, 'isUserFavourite', !isFavourite);
        }
        setIsFavourite(!isFavourite);
    };

    const { mutate: toggleFavouriteDashboard } = useToggleFavouriteDashboard(
        dashboard?.key,
        onSuccess,
    );

    const toggleFavourite = () => {
        if (!dashboard?.key) {
            console.error(
                'dashboard.key should be defined in onToggleFavourite',
            );
            return;
        }
        toggleFavouriteDashboard({ willBeFavourite: !isFavourite });
    };

    return {
        isFavourite,
        toggleFavourite,
    };
};
